/**
 * FinanceShowcase — Premium renderer for finance / budget / cash-flow layouts.
 * Palette: emerald income + rose expense + amber savings + violet goals.
 * Phase 8 — Athena Presentation Design System.
 */
import React, { useState } from 'react';
import { usePresentationBreakpoint, AthenaTip, ActionChips, SectionCard, MetricStrip, HeroPhotoPanel }
  from '../presentation-system';
import { base } from '../presentation-system/tokens/colors';
import { ProseText } from './ProseText';

const C = {
  income:       '#34d399',
  incomeFade:   'rgba(52,211,153,0.12)',
  incomeBorder: 'rgba(52,211,153,0.25)',
  expense:      '#f43f5e',
  expenseFade:  'rgba(244,63,94,0.12)',
  expenseBorder:'rgba(244,63,94,0.25)',
  saving:       '#fbbf24',
  savingFade:   'rgba(251,191,36,0.12)',
  savingBorder: 'rgba(251,191,36,0.25)',
  goal:         '#c084fc',
  goalFade:     'rgba(192,132,252,0.12)',
  goalBorder:   'rgba(192,132,252,0.25)',
  accent:       '#34d399',
  card:         base.card,
  border:       base.border,
  text:         base.text,
  textMuted:    base.textMuted,
  textFaint:    base.textFaint,
};

const _card = {
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 14,
  boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
};

const TYPE_STYLE = {
  income:  { color: C.income,  fade: C.incomeFade,  border: C.incomeBorder,  icon: '📈', label: 'Receita' },
  expense: { color: C.expense, fade: C.expenseFade, border: C.expenseBorder, icon: '📉', label: 'Despesa' },
  saving:  { color: C.saving,  fade: C.savingFade,  border: C.savingBorder,  icon: '🏦', label: 'Poupança' },
};

function HeroDesktop({ d }) {
  const titleFs = (d.title || '').length > 38 ? 21 : (d.title || '').length > 24 ? 26 : 30;
  const ov = d.overview || {};
  const METRICS = [
    { icon: '📅', value: d.period    || 'Período',    label: 'Período'  },
    ov.income   && { icon: '📈', value: ov.income,    label: 'Receita'  },
    ov.expenses && { icon: '📉', value: ov.expenses,  label: 'Despesa'  },
    ov.balance  && { icon: '⚖️', value: ov.balance,   label: 'Saldo'    },
  ].filter(Boolean).slice(0, 3);

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 14, overflow: 'hidden', display: 'flex', height: 260,
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <HeroPhotoPanel
        heroImage={d.hero_image}
        badge="💰 FINANÇAS"
        accentColor={C.income} accentFade={C.incomeFade} accentBdr={C.incomeBorder}
        placeholder="💰" placeholderLabel="Finanças"
        title={d.title}
      />
      <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: titleFs, fontWeight: 800, color: C.text, lineHeight: 1.2, letterSpacing: '-.02em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: 0, fontSize: 13, color: base.textSec, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
        </div>
        <MetricStrip metrics={METRICS} accentColor={C.income} />
      </div>
    </div>
  );
}

function HeroMobile({ d }) {
  const ov = d.overview || {};
  const METRICS = [
    { icon: '📅', value: d.period    || 'Período',    label: 'Período'  },
    { icon: '📈', value: ov.income   || 'A apurar',   label: 'Receita'  },
    { icon: '📉', value: ov.expenses || 'A apurar',   label: 'Despesa'  },
    { icon: '⚖️', value: ov.balance  || 'A apurar',   label: 'Saldo'    },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 12, overflow: 'hidden',
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', minHeight: 202 }}>
        <div style={{ flex: 1, minWidth: 0, padding: '20px 10px 18px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <span style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: C.income, background: C.incomeFade, border: `1px solid ${C.incomeBorder}`, borderRadius: 5, padding: '2px 8px', marginBottom: 10 }}>💰 FINANÇAS</span>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.25 }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: '6px 0 0', fontSize: 12, color: base.textSec, lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
        </div>
        <HeroPhotoPanel
          heroImage={d.hero_image} isMobile
          accentColor={C.income} accentFade={C.incomeFade} accentBdr={C.incomeBorder}
          placeholder="💰"
          title={d.title}
        />
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.18)' }}>
        <MetricStrip metrics={METRICS} bordered accentColor={C.income} />
      </div>
    </div>
  );
}

function OverviewGrid({ overview, currency, isMobile }) {
  const curr = currency || 'R$';
  const items = [
    { key: 'income',   label: 'Receitas',  icon: '📈', color: C.income  },
    { key: 'expenses', label: 'Despesas',  icon: '📉', color: C.expense },
    { key: 'balance',  label: 'Saldo',     icon: '⚖️', color: C.saving  },
    { key: 'savings',  label: 'Reserva',   icon: '🏦', color: C.goal    },
  ].filter(it => overview[it.key]);

  if (items.length === 0) return null;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : `repeat(${Math.min(items.length, 4)}, 1fr)`,
      gap: 8,
      marginBottom: 14,
    }}>
      {items.map(it => (
        <div key={it.key} style={{
          ..._card, padding: '12px 14px',
          borderLeft: `3px solid ${it.color}`,
        }}>
          <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 4 }}>{it.icon} {it.label}</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: it.color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
            {overview[it.key]}
          </div>
        </div>
      ))}
    </div>
  );
}

function CategoryBar({ cat }) {
  const ts = TYPE_STYLE[cat.type] || TYPE_STYLE.expense;
  const pct = cat.percentage != null ? Math.min(Math.max(cat.percentage, 0), 100) : null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13 }}>{ts.icon}</span>
          <span style={{ fontSize: 13, color: C.text }}>{cat.name}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {pct != null && (
            <span style={{ fontSize: 11, color: C.textFaint }}>{pct.toFixed(0)}%</span>
          )}
          {cat.amount && (
            <span style={{ fontSize: 13, fontWeight: 700, color: ts.color, fontVariantNumeric: 'tabular-nums' }}>{cat.amount}</span>
          )}
        </div>
      </div>
      {pct != null && (
        <div style={{ height: 5, background: 'rgba(148,163,184,0.1)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: ts.color, borderRadius: 3, opacity: 0.8 }} />
        </div>
      )}
    </div>
  );
}

function GoalCard({ goal }) {
  const pct = goal.percentage != null ? Math.min(Math.max(goal.percentage, 0), 100) : null;
  return (
    <div style={{ ..._card, padding: '12px 14px', borderLeft: `3px solid ${C.goal}`, marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: pct != null ? 8 : 0 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{goal.name}</div>
          {goal.current && goal.target && (
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{goal.current} de {goal.target}</div>
          )}
          {!goal.current && goal.target && (
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Meta: {goal.target}</div>
          )}
        </div>
        {pct != null && (
          <span style={{ fontSize: 14, fontWeight: 800, color: C.goal }}>{pct.toFixed(0)}%</span>
        )}
      </div>
      {pct != null && (
        <div style={{ height: 5, background: 'rgba(192,132,252,0.1)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: C.goal, borderRadius: 3, opacity: 0.8 }} />
        </div>
      )}
    </div>
  );
}

function TransactionRow({ tx }) {
  const isIn = tx.type === 'in';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 14, color: isIn ? C.income : C.expense }}>{isIn ? '↑' : '↓'}</span>
      <div style={{ flex: 1, fontSize: 13, color: C.text }}>{tx.description}</div>
      {tx.date && <div style={{ fontSize: 11, color: C.textFaint }}>{tx.date}</div>}
      <div style={{ fontSize: 13, fontWeight: 700, color: isIn ? C.income : C.expense, fontVariantNumeric: 'tabular-nums' }}>{tx.amount}</div>
    </div>
  );
}

export default function FinanceShowcase({ data, wp, onAction }) {
  const isMobile = usePresentationBreakpoint();
  const d = wp || data;
  if (!d) return null;

  const categories   = d.categories   || [];
  const goals        = d.goals        || [];
  const transactions = d.transactions || [];
  const overview     = d.overview     || {};

  const expenses = categories.filter(c => c.type !== 'income' && c.type !== 'saving');
  const incomes  = categories.filter(c => c.type === 'income');
  const savings  = categories.filter(c => c.type === 'saving');

  return (
    <div style={{ color: C.text, fontFamily: 'inherit', lineHeight: 1.6, maxWidth: '100%' }}>
      {isMobile ? <HeroMobile d={d} /> : <HeroDesktop d={d} />}

      {d.context && !d.subtitle && (
        <div style={{ ..._card, padding: '12px 14px', marginBottom: 12, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>
          {d.context}
        </div>
      )}

      {Object.keys(overview).length > 0 && (
        <OverviewGrid overview={overview} currency={d.currency} isMobile={isMobile} />
      )}

      {expenses.length > 0 && (
        <SectionCard title="Despesas" borderColor={C.expenseBorder} style={{ marginBottom: 10 }}>
          {expenses.map((cat, i) => <CategoryBar key={i} cat={cat} />)}
        </SectionCard>
      )}

      {incomes.length > 0 && (
        <SectionCard title="Receitas" borderColor={C.incomeBorder} style={{ marginBottom: 10 }}>
          {incomes.map((cat, i) => <CategoryBar key={i} cat={cat} />)}
        </SectionCard>
      )}

      {savings.length > 0 && (
        <SectionCard title="Poupança & Investimentos" borderColor={C.savingBorder} style={{ marginBottom: 10 }}>
          {savings.map((cat, i) => <CategoryBar key={i} cat={cat} />)}
        </SectionCard>
      )}

      {goals.length > 0 && (
        <SectionCard title="Metas Financeiras" borderColor={C.goalBorder} style={{ marginBottom: 10 }}>
          {goals.map((g, i) => <GoalCard key={i} goal={g} />)}
        </SectionCard>
      )}

      {transactions.length > 0 && (
        <SectionCard title="Movimentações" borderColor={C.border} style={{ marginBottom: 10 }}>
          <div>
            {transactions.map((tx, i) => <TransactionRow key={i} tx={tx} />)}
          </div>
        </SectionCard>
      )}

      {(d.sections || []).map((s, i) => (
        <SectionCard key={i} title={s.title} borderColor={C.border} style={{ marginBottom: 10 }}>
          <ProseText text={s.content} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        </SectionCard>
      ))}

      <AthenaTip tip={d.tip} accentColor={C.income} style={{ marginBottom: 14 }} />
      <ActionChips chips={d.follow_up_chips} onAction={onAction} accentColor={C.income} />
    </div>
  );
}
